import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Button } from 'reactstrap';

import globalTranslations from '../../../../i18n/globalTranslations';
import { modelOf } from '../../../../prop-types';
import {
  getStorageAndParse,
  stringifyAndSetStorage,
} from '../../../../util/storage';
import Filter from '../../../../models/Filter';
import UIStore from '../../../../store/UIStore';
import FiltersOffCanvas from '../../../product-list/filters/FiltersOffCanvas';
import Icon from '../../../common/Icon';

export const STORAGE_KEY = 'checkoutCartEnabledFilters';

/** Canonical value for "no category" in filter state; use this for null/undefined/''/'0'. */
export const EMPTY_CATEGORY_VALUE = 'null';

/**
 * Normalize category filter value for storage and comparison.
 * Empty category (null, undefined, '', '0') → EMPTY_CATEGORY_VALUE; otherwise → String(value).
 */
export const normalizeCategoryFilterValue = (categoryValue) => {
  if (
    categoryValue === null ||
    categoryValue === undefined ||
    categoryValue === '' ||
    categoryValue === '0'
  ) {
    return EMPTY_CATEGORY_VALUE;
  }
  return String(categoryValue);
};

// Export utility function to get enabled filters
export const getEnabledFilters = () => {
  try {
    const stored = getStorageAndParse(STORAGE_KEY);
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
};

const CheckoutCartCategoryFilters = ({
  uiStore,
  intl,
  filter,
  onFiltersChange,
}) => {
  const filterOptions = filter?.options || [];
  const [enabledFilters, setEnabledFilters] = useState(getEnabledFilters);
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false);
  const onFiltersChangeRef = useRef(onFiltersChange);
  const isInitialMount = useRef(true);

  // Threshold: show buttons if 4 or less on desktop, 2 or less on mobile
  const filterThreshold = uiStore.isDesktop ? 4 : 2;
  const shouldUseOffCanvas =
    uiStore.isMobile || filterOptions.length > filterThreshold;

  // Keep the callback ref up to date
  useEffect(() => {
    onFiltersChangeRef.current = onFiltersChange;
  }, [onFiltersChange]);

  // Update localStorage when enabled filters change
  useEffect(() => {
    stringifyAndSetStorage(STORAGE_KEY, enabledFilters);

    // Skip callback on initial mount, only call when filters actually change
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Notify parent component of filter changes
    if (onFiltersChangeRef.current) {
      onFiltersChangeRef.current(enabledFilters);
    }
  }, [enabledFilters]);

  // Use shared normalization so cart and filters agree on empty-category value
  const normalizeCategoryValue = normalizeCategoryFilterValue;

  const toggleFilter = (categoryValue) => {
    const categoryIdString = normalizeCategoryValue(categoryValue);

    setEnabledFilters((prev) => {
      const isEnabled = prev.includes(categoryIdString);
      if (isEnabled) {
        // Remove filter
        return prev.filter((id) => id !== categoryIdString);
      } else {
        // Add filter
        return [...prev, categoryIdString];
      }
    });
  };

  const clearAllFilters = () => {
    setEnabledFilters([]);
  };

  const isFilterEnabled = (categoryValue) => {
    const categoryIdString = normalizeCategoryValue(categoryValue);
    return enabledFilters.includes(categoryIdString);
  };

  const hasActiveFilters = enabledFilters.length > 0;

  const toggleOffCanvas = () => {
    setIsOffCanvasOpen(!isOffCanvasOpen);
  };

  const renderFilterButtons = () => {
    return (
      <>
        <Button
          color="secondary"
          onClick={clearAllFilters}
          className={classNames(
            'CheckoutCartCategoryFilters__all-products-button',
            {
              'CheckoutCartCategoryFilters__all-products-button--selected':
                !hasActiveFilters,
            }
          )}
        >
          <FormattedMessage {...globalTranslations.showAllProductsSentence} />
          {` (${filter.totalCount})`}
        </Button>
        {filterOptions.map((option) => {
          const isEnabled = isFilterEnabled(option.value);
          const isNullCategory =
            normalizeCategoryFilterValue(option.value) === EMPTY_CATEGORY_VALUE;
          const label = isNullCategory ? (
            <FormattedMessage {...globalTranslations.noCategoryTitle} />
          ) : (
            option.name
          );
          const key = String(option.value ?? option.name ?? 'null');
          const countText = ` (${option.getCount})`;

          return (
            <Button
              key={key}
              color="secondary"
              onClick={() => toggleFilter(option.value)}
              className={classNames(
                'CheckoutCartCategoryFilters__filter-button',
                {
                  'CheckoutCartCategoryFilters__filter-button--selected':
                    isEnabled,
                }
              )}
            >
              {label}
              {countText}
            </Button>
          );
        })}
      </>
    );
  };

  const renderOffCanvasContent = () => {
    return (
      <div className="CheckoutCartCategoryFilters__offcanvas-content">
        <div className="CheckoutCartCategoryFilters__offcanvas-header">
          <h3>
            <FormattedMessage {...globalTranslations.allFiltersTitle} />
          </h3>
          <Button
            color="link"
            onClick={toggleOffCanvas}
            className="CheckoutCartCategoryFilters__offcanvas-close"
            aria-label={intl.formatMessage(globalTranslations.closeTitle)}
          >
            <Icon name="times" />
          </Button>
        </div>
        <div className="CheckoutCartCategoryFilters__offcanvas-body">
          <div className="CheckoutCartCategoryFilters__buttons">
            {renderFilterButtons()}
          </div>
        </div>
      </div>
    );
  };

  if (shouldUseOffCanvas) {
    return (
      <>
        <div className="CheckoutCartCategoryFilters">
          <Button
            color="secondary"
            onClick={toggleOffCanvas}
            className="CheckoutCartCategoryFilters__offcanvas-trigger"
          >
            <Icon name="filter" />
            <FormattedMessage {...globalTranslations.allFiltersTitle} />
            {hasActiveFilters && (
              <span className="CheckoutCartCategoryFilters__active-badge">
                {enabledFilters.length}
              </span>
            )}
          </Button>
        </div>
        <FiltersOffCanvas open={isOffCanvasOpen} handleToggle={toggleOffCanvas}>
          {renderOffCanvasContent()}
        </FiltersOffCanvas>
      </>
    );
  }

  return (
    <div className="CheckoutCartCategoryFilters">
      <div className="CheckoutCartCategoryFilters__buttons">
        {renderFilterButtons()}
      </div>
    </div>
  );
};

CheckoutCartCategoryFilters.propTypes = {
  uiStore: modelOf(UIStore).isRequired,
  intl: intlShape.isRequired,
  filter: PropTypes.oneOfType([modelOf(Filter), PropTypes.object]).isRequired,
  onFiltersChange: PropTypes.func,
};

export default injectIntl(
  inject('uiStore')(observer(CheckoutCartCategoryFilters))
);
